import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { captureException } from '@sentry/angular';
import { AccountService } from '@wilson/account';
import {
  CirculationGroupGateway,
  TimelineStayGateway,
} from '@wilson/api/gateway';
import { FeatureFlagPurePipe } from '@wilson/feature-flags';
import {
  CirculationGroupWithShiftAndStay,
  OrganizationalUnit,
  Shift,
  Stay,
  StayStatus,
  TimelineShift,
  User,
} from '@wilson/interfaces';
import { LoadingStateComponent } from '@wilson/shared-components';
import { CirculationGroupItemsSelectionHelper } from '@wilson/shift-timeline/assignments-gantt-chart';
import { isShiftDraggable } from '@wilson/shift-timeline/pipes';
import {
  CreateNewViaDragAndDropService,
  DragAndDropModeService,
} from '@wilson/shift-timeline/services';
import {
  SetDemarcatorRange,
  ShiftTimelineDataState,
  ShiftTimelineUnassignedRegionState,
  TimelineRemoveShiftFromUserV2,
  TimelineRemoveStay,
  TimelineStaysSuggestionService,
  TimelineUpsertNewStay,
} from '@wilson/shift-timeline/state';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { firstValueFrom } from 'rxjs';
import { TimelineShiftAssignService } from '../../services/timeline-shift-assign.service';
import { UnassignShiftCommonOperationsService } from '../../services/unassign-shift-common-operations.service';
import { BulkShiftAccessRightConfirmationModalComponent } from '../bulk-shift-access-right-confirmation-modal/bulk-shift-access-right-confirmation-modal.component';
import { ShiftAccessRightConfirmationModalResult } from '../shift-access-right-confirmation-modal/shift-access-right-confirmation-modal.component';
import {
  GroupAssignmentConfirmationModalComponent,
  GroupAssignmentConfirmationModalResult,
} from './group-assignment-confirmation-modal.component';
import { GroupAssignmentModalOperation } from './group-assignment-modal-data';
import { GroupAssignmentTimelineView } from './group-assignment-timeline-view';

export interface GroupAssignmentConfirmation {
  decision: GroupAssignmentConfirmationModalResult | null;
  groupItems: CirculationGroupWithShiftAndStay[];
}

export interface AssignAllGroupItemsParams {
  userId: string;
  draggingShift: TimelineShift;
  groupItems: CirculationGroupWithShiftAndStay[];
}

export interface BuildShiftsForGroupUnassignParams {
  draggingShift: TimelineShift;
  groupItems: CirculationGroupWithShiftAndStay[];
}

export interface UnassignAllGroupItemsParams
  extends BuildShiftsForGroupUnassignParams {
  shiftsToUnassign?: TimelineShift[];
}

@Injectable()
export class GroupAssignmentHelperService {
  private readonly store = inject(Store);
  private readonly featureFlagPurePipe = inject(FeatureFlagPurePipe);
  private readonly circulationGroupGateway = inject(CirculationGroupGateway);
  private readonly timelineStayGateway = inject(TimelineStayGateway);
  private readonly messageService = inject(NzMessageService);
  private readonly translate = inject(TranslateService);
  private readonly modalService = inject(NzModalService);
  private readonly accountService = inject(AccountService);
  private readonly timelineShiftAssignService = inject(
    TimelineShiftAssignService,
  );
  private readonly dragAndDropModeService = inject(DragAndDropModeService);
  private readonly createNewViaDragAndDropService = inject(
    CreateNewViaDragAndDropService,
  );
  private readonly timelineStaysSuggestionService = inject(
    TimelineStaysSuggestionService,
  );
  private readonly unassignShiftCommonOperationsService = inject(
    UnassignShiftCommonOperationsService,
  );
  private readonly circulationGroupItemsSelectionHelper = inject(
    CirculationGroupItemsSelectionHelper,
  );

  private readonly groupItemsCache = new Map<
    string,
    { items: CirculationGroupWithShiftAndStay[]; fetchedAt: number }
  >();

  private static readonly GROUP_ITEMS_CACHE_TTL_MS = 60_000;

  private readonly isCirculationGroupingEnabled = toSignal(
    this.featureFlagPurePipe.transform('portal-circulation-grouping'),
    { initialValue: false },
  );

  private readonly standardTimelineView: GroupAssignmentTimelineView = {
    resolveShiftFromTimelineState: (shift) =>
      this.resolveStandardViewShiftFromTimelineState(shift),
    assignShiftsWithoutActivities: async (userId, shifts, draggingShiftId) => {
      if (shifts.length > 0) {
        this.createNewViaDragAndDropService.addUpdatingId(draggingShiftId);
        await this.timelineShiftAssignService.handleStandardViewShiftsWithoutActivitiesAssignments(
          userId,
          shifts,
        );
        this.store.dispatch(new SetDemarcatorRange([]));
      }
    },
    unassignShifts: (shifts) =>
      this.unassignShiftCommonOperationsService.handleStandardViewUnassign(
        shifts,
      ),
    undoUnassign: (shifts) =>
      this.unassignShiftCommonOperationsService.undoStandardViewUnassign(
        shifts,
      ),
    getBulkAssignUpdatingId: (draggingShiftId, shiftsToAssignCount) =>
      shiftsToAssignCount > 0 ? draggingShiftId : null,
  };

  private readonly cellTimelineView: GroupAssignmentTimelineView = {
    resolveShiftFromTimelineState: (shift) =>
      this.resolveCellViewShiftFromTimelineState(shift),
    assignShiftsWithoutActivities: async (userId, shifts) => {
      if (shifts.length > 0) {
        await this.timelineShiftAssignService.handleCellViewShiftsWithoutActivitiesAssignments(
          userId,
          shifts,
        );
      }
    },
    unassignShifts: (shifts) =>
      this.unassignShiftCommonOperationsService.handleCellViewUnassign(shifts),
    undoUnassign: (shifts) =>
      this.unassignShiftCommonOperationsService.undoCellViewUnassign(shifts),
    getBulkAssignUpdatingId: () => null,
  };

  public checkAndConfirmGroupAssignment(
    shiftId: string,
  ): Promise<GroupAssignmentConfirmation> {
    return this.checkAndConfirmGroupOperation(shiftId, 'assign');
  }

  public checkAndConfirmGroupUnassignment(
    shiftId: string,
  ): Promise<GroupAssignmentConfirmation> {
    return this.checkAndConfirmGroupOperation(shiftId, 'unassign');
  }

  private async checkAndConfirmGroupOperation(
    shiftId: string,
    operation: GroupAssignmentModalOperation,
  ): Promise<GroupAssignmentConfirmation> {
    let decision: GroupAssignmentConfirmationModalResult | null =
      GroupAssignmentConfirmationModalResult.MoveSelectedItem;
    let groupItems: CirculationGroupWithShiftAndStay[] = [];

    if (this.isCirculationGroupingEnabled()) {
      const fetchedGroupItems = await this.getGroupItems(shiftId);

      if (fetchedGroupItems === null) {
        decision = null;
        groupItems = [];
      } else {
        groupItems = fetchedGroupItems;

        if (groupItems.length > 0) {
          const titleKey =
            operation === 'unassign'
              ? 'page.shift_timeline.unassign_shift.group_assignment.title'
              : 'page.shift_timeline.assign_shift.group_assignment.title';

          const modalRef = this.modalService.create({
            nzTitle: this.translate.instant(titleKey),
            nzContent: GroupAssignmentConfirmationModalComponent,
            nzData: { operation },
            nzMaskClosable: false,
            nzKeyboard: false,
          });

          decision = await firstValueFrom(modalRef.afterClose);
        }
      }
    }

    return { decision, groupItems };
  }

  public buildShiftsForGroupUnassignInStandardView(
    params: BuildShiftsForGroupUnassignParams,
  ): TimelineShift[] {
    return this.buildShiftsForGroupUnassign(params, this.standardTimelineView);
  }

  public buildShiftsForGroupUnassignInCellView(
    params: BuildShiftsForGroupUnassignParams,
  ): TimelineShift[] {
    return this.buildShiftsForGroupUnassign(params, this.cellTimelineView);
  }

  public async unassignAllGroupItemsInStandardView(
    params: UnassignAllGroupItemsParams,
  ): Promise<void> {
    return this.unassignAllGroupItems(params, this.standardTimelineView);
  }

  public async unassignAllGroupItemsInCellView(
    params: UnassignAllGroupItemsParams,
  ): Promise<void> {
    return this.unassignAllGroupItems(params, this.cellTimelineView);
  }

  public async assignAllGroupItemsInStandardView(
    params: AssignAllGroupItemsParams,
  ): Promise<void> {
    return this.assignAllGroupItems(params, this.standardTimelineView);
  }

  public async assignAllGroupItemsInCellView(
    params: AssignAllGroupItemsParams,
  ): Promise<void> {
    return this.assignAllGroupItems(params, this.cellTimelineView);
  }

  private buildShiftsForGroupUnassign(
    { draggingShift, groupItems }: BuildShiftsForGroupUnassignParams,
    view: GroupAssignmentTimelineView,
  ): TimelineShift[] {
    const groupShiftIds = new Set<string>();
    const shiftsToUnassign: TimelineShift[] = groupItems
      .map((row) => row.shift)
      .filter((shift): shift is Shift & { id: string } => !!shift?.id)
      .filter((shift) => {
        if (groupShiftIds.has(shift.id)) {
          return false;
        }
        groupShiftIds.add(shift.id);
        return true;
      })
      .map((shift) => view.resolveShiftFromTimelineState(shift))
      .filter((shift) => !!shift.userId);

    if (!groupShiftIds.has(draggingShift.id) && draggingShift.userId) {
      shiftsToUnassign.push(
        view.resolveShiftFromTimelineState(
          draggingShift as Shift & { id: string },
        ),
      );
    }

    return shiftsToUnassign.filter((shift) => isShiftDraggable(shift));
  }

  private async unassignAllGroupItems(
    {
      draggingShift,
      groupItems,
      shiftsToUnassign: prebuiltShiftsToUnassign,
    }: UnassignAllGroupItemsParams,
    view: GroupAssignmentTimelineView,
  ): Promise<void> {
    const shiftsToUnassign = (
      prebuiltShiftsToUnassign ??
      this.buildShiftsForGroupUnassign({ draggingShift, groupItems }, view)
    ).filter((shift) => isShiftDraggable(shift));

    let loadingModalRef: NzModalRef | null = null;

    if (shiftsToUnassign.length > 0) {
      try {
        loadingModalRef = this.openMoveAllLoaderDialog();

        await view.unassignShifts(shiftsToUnassign);

        this.dragAndDropModeService.setDraggingShiftV2(null);
        this.dragAndDropModeService.setDraggingItemId(null);
        this.store.dispatch(new SetDemarcatorRange([]));
      } catch (error) {
        captureException(error);
        this.messageService.error(
          this.translate.instant('general.something_went_wrong'),
        );
        view.undoUnassign(shiftsToUnassign);
        this.dragAndDropModeService.setDraggingShiftV2(null);
        this.dragAndDropModeService.setDraggingItemId(null);
        this.store.dispatch(new SetDemarcatorRange([]));
      } finally {
        loadingModalRef?.destroy();
      }
    }
  }

  private async assignAllGroupItems(
    { userId, draggingShift, groupItems }: AssignAllGroupItemsParams,
    view: GroupAssignmentTimelineView,
  ): Promise<void> {
    const groupShiftIds = new Set<string>();
    const groupShifts = groupItems
      .map((row) => row.shift)
      .filter((shift): shift is Shift & { id: string } => !!shift?.id)
      .filter((shift) => {
        if (groupShiftIds.has(shift.id)) {
          return false;
        }
        groupShiftIds.add(shift.id);
        return true;
      })
      .map((shift) => view.resolveShiftFromTimelineState(shift));

    let loadingModalRef: NzModalRef | null = null;
    let updatingId: string | null = null;

    try {
      const user = (await firstValueFrom(
        this.accountService.get(userId, {
          relations: ['accessibleOrganizationalUnits'],
        }),
      )) as User & {
        accessibleOrganizationalUnits: OrganizationalUnit[];
      };

      const accessibleOrganizationalUnitIds = new Set(
        user.accessibleOrganizationalUnits
          .map((organizationalUnit) => organizationalUnit.id)
          .filter((id): id is string => !!id),
      );

      const hasAnyOrganizationalUnitMismatch = groupShifts.some(
        (shift) =>
          !accessibleOrganizationalUnitIds.has(shift.organizationalUnitId),
      );

      let accessDecision: ShiftAccessRightConfirmationModalResult | null =
        ShiftAccessRightConfirmationModalResult.ProceedAnyway;

      if (hasAnyOrganizationalUnitMismatch) {
        accessDecision = await this.confirmOrganizationalUnitMismatchDecision();
      }

      if (!accessDecision) {
        this.clearDraggingState(userId);
      } else {
        const shiftsToAssign = groupShifts.map((shift) =>
          this.applyOrganizationalUnitAccessDecision(
            shift,
            accessDecision,
            accessibleOrganizationalUnitIds,
            user.organizationalUnitId,
          ),
        );

        if (!groupShiftIds.has(draggingShift.id)) {
          shiftsToAssign.push(
            this.applyOrganizationalUnitAccessDecision(
              view.resolveShiftFromTimelineState(
                draggingShift as Shift & { id: string },
              ),
              accessDecision,
              accessibleOrganizationalUnitIds,
              user.organizationalUnitId,
            ),
          );
        }

        const groupStays = groupItems
          .map((row) => row.stay)
          .filter((stay): stay is Stay => !!stay);

        const eligibleShiftsToAssign = shiftsToAssign.filter((shift) =>
          isShiftDraggable(shift),
        );

        if (eligibleShiftsToAssign.length === 0 && groupStays.length === 0) {
          this.clearDraggingState(userId);
        } else {
          this.store.dispatch(
            new TimelineRemoveShiftFromUserV2({
              userId: userId,
              shiftId: CreateNewViaDragAndDropService.MOCK_SHIFT_ID,
            }),
          );

          if (eligibleShiftsToAssign.length > 0) {
            loadingModalRef = this.openMoveAllLoaderDialog();
            updatingId = view.getBulkAssignUpdatingId(
              draggingShift.id,
              eligibleShiftsToAssign.length,
            );

            await view.assignShiftsWithoutActivities(
              userId,
              eligibleShiftsToAssign,
              draggingShift.id,
            );
          }

          if (groupStays.length > 0) {
            await this.assignStaysToNewUser(groupStays, userId);
          }

          this.dragAndDropModeService.setDraggingShiftV2(null);
          this.dragAndDropModeService.setDraggingItemId(null);
        }
      }
    } catch (error) {
      captureException(error);
      this.messageService.error(
        this.translate.instant('general.something_went_wrong'),
      );
      this.clearDraggingState(userId);
    } finally {
      loadingModalRef?.destroy();
      if (updatingId) {
        this.createNewViaDragAndDropService.removeUpdatingId(updatingId);
      }
    }
  }

  private applyOrganizationalUnitAccessDecision(
    shift: TimelineShift,
    accessDecision: ShiftAccessRightConfirmationModalResult,
    accessibleOrganizationalUnitIds: Set<string>,
    userOrganizationalUnitId: string,
  ): TimelineShift {
    if (
      accessDecision === ShiftAccessRightConfirmationModalResult.UpdateShift &&
      !accessibleOrganizationalUnitIds.has(shift.organizationalUnitId)
    ) {
      return {
        ...shift,
        organizationalUnitId: userOrganizationalUnitId,
      };
    }
    return shift;
  }

  private resolveStandardViewShiftFromTimelineState(
    shift: Shift & { id: string },
  ): TimelineShift {
    const assignedShift = this.store.selectSnapshot(
      ShiftTimelineDataState.getShiftWithoutActivities(shift.id),
    );
    const unassignedShift = this.store.selectSnapshot(
      ShiftTimelineUnassignedRegionState.getShiftWithoutActivities(shift.id),
    );
    const fallbackUnassignedShift = this.store.selectSnapshot(
      ShiftTimelineUnassignedRegionState.getCellViewShiftWithoutActivities(
        shift.id,
      ),
    );

    if (assignedShift) {
      return this.normalizeTimelineShift(assignedShift);
    } else if (unassignedShift) {
      return this.normalizeTimelineShift(unassignedShift);
    } else if (fallbackUnassignedShift) {
      return this.normalizeTimelineShift(fallbackUnassignedShift);
    } else {
      return this.normalizeTimelineShift(shift);
    }
  }

  private resolveCellViewShiftFromTimelineState(
    shift: Shift & { id: string },
  ): TimelineShift {
    const assignedShift = this.store.selectSnapshot(
      ShiftTimelineDataState.getShiftWithoutActivities(shift.id),
    );
    const unassignedShift = this.store.selectSnapshot(
      ShiftTimelineUnassignedRegionState.getCellViewShiftWithoutActivities(
        shift.id,
      ),
    );
    const fallbackUnassignedShift = this.store.selectSnapshot(
      ShiftTimelineUnassignedRegionState.getShiftWithoutActivities(shift.id),
    );

    if (assignedShift) {
      return this.normalizeTimelineShift(assignedShift);
    } else if (unassignedShift) {
      return this.normalizeTimelineShift(unassignedShift);
    } else if (fallbackUnassignedShift) {
      return this.normalizeTimelineShift(fallbackUnassignedShift);
    } else {
      return this.normalizeTimelineShift(shift);
    }
  }

  private normalizeTimelineShift(shift: Partial<TimelineShift>): TimelineShift {
    const startLocation = shift.startLocation
      ? {
          ...shift.startLocation,
          locationCode: shift.startLocation.locationCode ?? '',
          name: shift.startLocation.name ?? '',
        }
      : { locationCode: '', name: '' };
    const endLocation = shift.endLocation
      ? {
          ...shift.endLocation,
          locationCode: shift.endLocation.locationCode ?? '',
          name: shift.endLocation.name ?? '',
        }
      : { locationCode: '', name: '' };

    return {
      ...shift,
      startLocation,
      endLocation,
      isHighlighted: shift.isHighlighted ?? false,
    } as TimelineShift;
  }

  private async assignStaysToNewUser(
    stays: Stay[],
    userId: string,
  ): Promise<void> {
    const previousUserIdByStayId = new Map(
      stays.map((stay) => [stay.id, stay.userId]),
    );

    const updatedStays = await firstValueFrom(
      this.timelineStayGateway.bulkUpdateStays(
        stays.map((stay) => ({
          id: stay.id,
          userId,
        })),
      ),
    );

    const staysToSync =
      updatedStays.length > 0
        ? updatedStays
        : stays.map((stay) => ({ ...stay, userId: userId }));

    for (const stay of staysToSync) {
      await this.updateStayInTimeline(
        stay,
        previousUserIdByStayId.get(stay.id),
      );
    }
  }

  private async updateStayInTimeline(
    stayToModify: Stay,
    previousUserId: string | undefined,
  ): Promise<void> {
    if (
      stayToModify.status === StayStatus.Cancelled ||
      stayToModify.status === StayStatus.ShortTermCancelled
    ) {
      this.store.dispatch(
        new TimelineRemoveStay({
          stayId: stayToModify.id,
          userId: stayToModify.userId,
        }),
      );
    } else {
      this.store.dispatch(new TimelineUpsertNewStay(stayToModify));
    }

    if (previousUserId && previousUserId !== stayToModify.userId) {
      this.store.dispatch(
        new TimelineRemoveStay({
          stayId: stayToModify.id,
          userId: previousUserId,
        }),
      );

      await this.timelineStaysSuggestionService.refreshStaysDataForSpecifiedUsers(
        [previousUserId],
      );
    }

    if (
      stayToModify.status !== StayStatus.Cancelled &&
      stayToModify.status !== StayStatus.ShortTermCancelled
    ) {
      await this.timelineStaysSuggestionService.refreshStaysDataForSpecifiedUsers(
        [stayToModify.userId],
      );
    }
  }

  private openMoveAllLoaderDialog(): NzModalRef {
    const modalRef = this.modalService.create({
      nzContent: LoadingStateComponent,
      nzCentered: true,
      nzKeyboard: false,
      nzClosable: false,
      nzMaskClosable: false,
      nzFooter: null,
    });

    if (modalRef.componentInstance) {
      modalRef.componentInstance.loadingMessageTranslationKey =
        'general.update.loading';
    }

    return modalRef;
  }

  private async confirmOrganizationalUnitMismatchDecision(): Promise<ShiftAccessRightConfirmationModalResult | null> {
    const modalRef = this.modalService.create({
      nzTitle: this.translate.instant(
        'page.shift_timeline.assign_shift.access_rights.title',
      ),
      nzContent: BulkShiftAccessRightConfirmationModalComponent,
      nzMaskClosable: false,
      nzKeyboard: false,
    });

    return firstValueFrom(modalRef.afterClose);
  }

  private clearDraggingState(targetUserId: string): void {
    this.store.dispatch(
      new TimelineRemoveShiftFromUserV2({
        userId: targetUserId,
        shiftId: CreateNewViaDragAndDropService.MOCK_SHIFT_ID,
      }),
    );
    this.dragAndDropModeService.setDraggingShiftV2(null);
    this.dragAndDropModeService.setDraggingItemId(null);
    this.store.dispatch(new SetDemarcatorRange([]));
  }

  private async getGroupItems(
    elementId: string,
  ): Promise<CirculationGroupWithShiftAndStay[] | null> {
    if (this.isCirculationGroupingEnabled()) {
      const selectionCachedItems =
        this.circulationGroupItemsSelectionHelper.getCachedGroupItemsForElement(
          elementId,
        );
      if (selectionCachedItems) {
        return selectionCachedItems;
      }
    }

    const cachedEntry = this.groupItemsCache.get(elementId);
    if (
      cachedEntry &&
      Date.now() - cachedEntry.fetchedAt <
        GroupAssignmentHelperService.GROUP_ITEMS_CACHE_TTL_MS
    ) {
      return cachedEntry.items;
    }

    try {
      const items = await firstValueFrom(
        this.circulationGroupGateway.getItems(elementId),
      );
      this.groupItemsCache.set(elementId, {
        items,
        fetchedAt: Date.now(),
      });
      return items;
    } catch (error) {
      captureException(error);
      this.messageService.error(
        this.translate.instant('general.something_went_wrong'),
      );
      return null;
    }
  }
}
